import React from 'react';
import { Button, Dialog, DialogContent, Stack, Typography } from '@mui/material';
import Avatar from 'components/@extended/Avatar';
import { StopOutlined, WarningOutlined, CheckOutlined, CloseOutlined, CloseCircleOutlined } from '@ant-design/icons';

interface Props {
  title: string | React.ReactNode;
  open: boolean;
  type?: 'error' | 'warning' | 'success';
  useYesNoButtonLabels?: boolean;
  handleClose: (status: boolean) => void;
}

export default function ActionConfirmation({ title, open, type, useYesNoButtonLabels = false, handleClose }: Props) {
  const getAvatarColor = () => {
    switch (type) {
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'success':
        return 'success';
      default:
        return 'default';
    }
  };

  const getAvatarIcon = () => {
    switch (type) {
      case 'error':
        return <StopOutlined />;
      case 'warning':
        return <WarningOutlined />;
      case 'success':
        return <CheckOutlined />;
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => handleClose(false)}
      keepMounted
      maxWidth="xs"
      aria-labelledby="action-confirmation-title"
      aria-describedby="action-confirmation-description"
      sx={{
        zIndex: 2000
      }}
    >
      {open && (
        <DialogContent sx={{ mt: 2, my: 1 }}>
          <Stack alignItems="center" spacing={3.5} justifyContent={'center'} alignContent={'center'}>
            <Avatar color={getAvatarColor()} sx={{ width: 48, height: 48, fontSize: '1.5rem' }}>
              {getAvatarIcon()}
            </Avatar>
            <Stack spacing={2}>
              <Typography fontSize={13} align="center" sx={{ whiteSpace: 'pre-line' }}>
                {title}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={2} sx={{ width: 1 }}>
              <Button
                startIcon={useYesNoButtonLabels ? <CloseOutlined /> : <CloseCircleOutlined />}
                fullWidth
                sx={{
                  bgcolor: 'maroon.main',
                  color: 'maroon.contrastText',
                  '&:hover': { bgcolor: 'maroon.dark' }
                }}
                variant="contained"
                onClick={() => handleClose(false)}
              >
                {useYesNoButtonLabels ? 'No' : 'Cancel'}
              </Button>
              <Button
                startIcon={<CheckOutlined />}
                fullWidth
                sx={{
                  bgcolor: 'forestgreen.main',
                  color: 'forestgreen.contrastText',
                  '&:hover': { bgcolor: 'forestgreen.dark' }
                }}
                variant="contained"
                onClick={() => handleClose(true)}
              >
                {useYesNoButtonLabels ? 'Yes' : 'Apply'}
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
      )}
    </Dialog>
  );
}